import { usePortalRole, type PortalRole } from "@/hooks/usePortalRole";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Eye, X } from "lucide-react";

const ROLE_LABELS: Record<Exclude<PortalRole, null>, string> = {
  admin: "Admin",
  consultant: "Consultant",
  installer: "Installer",
  customer: "Customer",
  partner: "Partner",
  marketing_manager: "Marketing",
  introducer: "Introducer",
  introducer_manager: "Introducer Manager",
  sales_manager: "Sales",
  customer_service: "Customer Service",
  surveyor: "Surveyor",
};

/**
 * Persistent banner shown to super admins when previewing another role.
 *
 * Honest by design: the banner makes clear that RLS still runs as the
 * real admin identity, so the view may show more data than a genuine
 * holder of the role would see.
 */
export default function ViewAsBanner() {
  const { role, isViewAs, exitViewAs, roles } = usePortalRole();
  const navigate = useNavigate();

  if (!isViewAs || !role) return null;

  const label = ROLE_LABELS[role] ?? role;
  const isAdminBased = roles.includes("admin");

  const handleExit = () => {
    exitViewAs();
    navigate("/portal/admin");
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-amber-100/90 dark:bg-amber-950/40 border-b border-amber-300/70 dark:border-amber-800/60 text-amber-900 dark:text-amber-100 text-xs sm:text-sm"
    >
      <div className="flex items-center gap-3 px-3 py-2 max-w-screen-2xl mx-auto">
        <Eye className="w-4 h-4 shrink-0" aria-hidden="true" />
        <p className="flex-1 min-w-0">
          <span className="font-semibold">Preview: {label}</span>
          {isAdminBased ? (
            <>
              {" "}— your owner access is still active. This previews the layout, not another person’s permissions. Use a separate test account to check access.
            </>
          ) : (
            <> — role-scoped view.</>
          )}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleExit}
          className="shrink-0 h-8 gap-1.5 text-amber-900 hover:bg-amber-200/60 dark:text-amber-100 dark:hover:bg-amber-900/40"
        >
          <X className="w-3.5 h-3.5" />
          Back to Owner
        </Button>
      </div>
    </div>
  );
}
